// Lista rapportini con filtri e download PDF
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FileText, Download, Search, Eye, Building2, MapPin } from 'lucide-react'
import type { Report } from '@fieldcore/types'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Table } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { EmptyState } from '../../components/ui/EmptyState'
import { ContextualHelp } from '../../components/shared/ContextualHelp'
import { formatDate } from '../../lib/utils'
import api from '../../lib/api'

type ReportWithRelations = Report & {
  ticket?: {
    company?: { id: string; name: string }
    site?: { id: string; name: string; city: string | null }
  }
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Bozza',
  completed: 'Completato',
  signed: 'Firmato',
  sent: 'Inviato',
}

export function ReportsListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [companySearch, setCompanySearch] = useState('')
  const [siteSearch, setSiteSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['reports', search, companySearch, siteSearch, status, page],
    queryFn: async () => {
      try {
        const params: Record<string, unknown> = { page, pageSize: 20 }
        if (status !== 'all') params['status'] = status
        if (companySearch) params['companyName'] = companySearch
        if (siteSearch) params['siteName'] = siteSearch

        const res = await api.get<{ items: ReportWithRelations[]; total: number; totalPages: number; page: number }>(
          '/reports',
          { params }
        )
        const payload = res.data
        let items = payload?.items ?? []
        if (search) {
          const q = search.toLowerCase()
          items = items.filter((r) =>
            r.reportNumber?.toLowerCase().includes(q) ||
            r.id?.toLowerCase().includes(q)
          )
        }
        return { ...payload, items }
      } catch {
        return { items: [] as ReportWithRelations[], total: 0, totalPages: 0, page: 1 }
      }
    },
  })

  const columns = [
    {
      key: 'number',
      header: '#',
      cell: (report: ReportWithRelations) => (
        <span className="font-mono text-xs text-[var(--fc-foreground-muted)]">
          {report.reportNumber}
        </span>
      ),
      className: 'w-28',
    },
    {
      key: 'company',
      header: 'Ragione sociale',
      cell: (report: ReportWithRelations) => (
        <span className="text-sm text-[var(--fc-foreground)]">
          {report.ticket?.company?.name ?? '—'}
        </span>
      ),
    },
    {
      key: 'site',
      header: 'Sede',
      cell: (report: ReportWithRelations) => {
        const site = report.ticket?.site
        if (!site) return <span className="text-sm text-[var(--fc-foreground-muted)]">—</span>
        return (
          <span className="text-sm text-[var(--fc-foreground)]">
            {site.name}{site.city ? ` — ${site.city}` : ''}
          </span>
        )
      },
    },
    {
      key: 'type',
      header: 'Tipo',
      cell: (report: ReportWithRelations) => <Badge variant="default">{report.type}</Badge>,
      className: 'w-36',
    },
    {
      key: 'status',
      header: 'Stato',
      cell: (report: ReportWithRelations) => (
        <Badge variant={report.status === 'signed' || report.status === 'sent' ? 'success' : 'warning'}>
          {STATUS_LABELS[report.status] ?? report.status}
        </Badge>
      ),
      className: 'w-28',
    },
    {
      key: 'signed',
      header: 'Firmato',
      cell: (report: ReportWithRelations) => (
        <Badge variant={report.customerSignatureUrl ? 'success' : 'default'}>
          {report.customerSignatureUrl ? 'Sì' : 'No'}
        </Badge>
      ),
      className: 'w-20',
    },
    {
      key: 'createdAt',
      header: 'Data',
      cell: (report: ReportWithRelations) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">
          {formatDate(report.createdAt, 'dd/MM/yyyy')}
        </span>
      ),
      className: 'w-28',
    },
    {
      key: 'actions',
      header: '',
      cell: (report: ReportWithRelations) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={(e) => { e.stopPropagation(); navigate(`/reports/${report.id}`) }}
            title="Visualizza"
          >
            <Eye className="h-4 w-4" />
          </Button>
          {report.pdfUrl && (
            <button
              onClick={async (e) => {
                e.stopPropagation()
                try {
                  const res = await api.get<Blob>(`/reports/${report.id}/pdf/download`, { responseType: 'blob' })
                  const blobUrl = URL.createObjectURL(res.data)
                  const a = document.createElement('a')
                  a.href = blobUrl
                  a.download = `rapportino-${report.reportNumber ?? report.id}.pdf`
                  a.click()
                  setTimeout(() => URL.revokeObjectURL(blobUrl), 10000)
                } catch {
                  alert('Errore download PDF')
                }
              }}
              className="flex h-8 w-8 items-center justify-center rounded text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)] transition-colors"
              aria-label="Scarica PDF"
            >
              <Download className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
      className: 'w-20',
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Rapportini"
        subtitle={data ? `${data.total} rapportini totali` : ''}
      />

      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[180px]">
          <Input
            placeholder="Cerca per numero..."
            leftIcon={<Search className="h-4 w-4" />}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          />
        </div>
        <div className="flex-1 min-w-[180px]">
          <Input
            placeholder="Ragione sociale..."
            leftIcon={<Building2 className="h-4 w-4" />}
            value={companySearch}
            onChange={(e) => { setCompanySearch(e.target.value); setPage(1) }}
          />
        </div>
        <div className="flex-1 min-w-[180px]">
          <Input
            placeholder="Sede..."
            leftIcon={<MapPin className="h-4 w-4" />}
            value={siteSearch}
            onChange={(e) => { setSiteSearch(e.target.value); setPage(1) }}
          />
        </div>
        <div className="w-44">
          <Select
            options={[
              { value: 'all', label: 'Tutti gli stati' },
              { value: 'draft', label: 'Bozza' },
              { value: 'completed', label: 'Completato' },
              { value: 'signed', label: 'Firmato' },
              { value: 'sent', label: 'Inviato' },
            ]}
            value={status}
            onValueChange={(v) => { setStatus(v); setPage(1) }}
          />
        </div>
      </div>

      {!isLoading && data?.items.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-12 w-12" />}
          title="Nessun rapportino"
          description="Non ci sono rapportini che corrispondono ai criteri di ricerca."
        />
      ) : (
        <Table
          data={data?.items ?? []}
          columns={columns}
          keyExtractor={(r) => r.id}
          isLoading={isLoading}
          onRowClick={(r) => navigate(`/reports/${r.id}`)}
        />
      )}

      <ContextualHelp />
    </div>
  )
}
